/**
 * MobileActionBar — the 4-action bar at the bottom of the mobile studio.
 *
 *   [Remix]   [Animate🔒]   [Share]   [Download]
 *   Studio    Premium       Device    Formats
 *
 * iter-73: per the spec, exactly 4 actions, each with a single helper
 * label underneath. No paragraphs, no nested CTAs. The action bar is
 * a sticky element so it's always reachable as the card scrolls.
 */
import React from "react";
import { motion } from "framer-motion";
import { Wand2, Play, Share2, Download, Lock } from "lucide-react";
import { tap } from "../../lib/haptic";

function ActionButton({
    label,
    helper,
    icon: Icon,
    accent,
    onClick,
    locked,
    iconColor,
    testid,
}) {
    return (
        <motion.button
            type="button"
            whileTap={{ scale: 0.96 }}
            onClick={() => { tap(); onClick?.(); }}
            className="flex-1 flex flex-col items-center justify-center gap-1.5 py-3 transition-opacity"
            data-testid={testid}
        >
            <div className="relative">
                <Icon
                    className={`w-7 h-7 ${iconColor || ""}`}
                    strokeWidth={1.6}
                    style={accent ? { color: accent } : undefined}
                />
                {locked && (
                    <span className="absolute -top-1 -right-2 w-4 h-4 rounded-full bg-black/85 border border-amber-300/55 flex items-center justify-center">
                        <Lock className="w-2 h-2 text-amber-300" strokeWidth={2.4} />
                    </span>
                )}
            </div>
            <div className="text-center leading-tight">
                <div className="text-[11px] font-medium text-white/95 tracking-wide">
                    {label}
                </div>
                <div className="text-[9px] tracking-[0.18em] uppercase text-white/40 mt-0.5">
                    {helper}
                </div>
            </div>
        </motion.button>
    );
}

export default function MobileActionBar({
    onRemix,
    onAnimate,
    onShare,
    onDownload,
    isPremium,
}) {
    return (
        <div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-20 px-3 pt-1 pointer-events-none"
            style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
            data-testid="mobile-action-bar-container"
        >
            <div
                className="pointer-events-auto mx-auto max-w-md rounded-2xl bg-[#101013]/90 backdrop-blur-xl border border-white/8 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.6)]"
                data-testid="mobile-action-bar"
            >
                <div className="flex items-stretch">
                    <ActionButton
                        label="Remix"
                        helper="studio"
                        icon={Wand2}
                        iconColor="text-violet-300"
                        onClick={onRemix}
                        testid="action-remix"
                    />
                    {/* iter-101 — Animate action SHELVED for MVP. Re-enable by
                        uncommenting the block below.
                    <ActionButton
                        label="Animate"
                        helper="premium"
                        icon={Play}
                        iconColor="text-sky-300"
                        locked={!isPremium}
                        onClick={onAnimate}
                        testid="action-animate"
                    />
                    */}
                    <ActionButton
                        label="Share"
                        helper="device"
                        icon={Share2}
                        iconColor="text-emerald-300"
                        onClick={onShare}
                        testid="action-share"
                    />
                    <ActionButton
                        label="Download"
                        helper="formats"
                        icon={Download}
                        iconColor="text-amber-300"
                        onClick={onDownload}
                        testid="action-download"
                    />
                </div>
            </div>
        </div>
    );
}
